import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'common/dayjs';

/* API */
import ReservationService from 'api/ReservationService';
import { ReservationResponseDto as ReservationDetail } from 'api/generated/openapi';
import TenpoService from 'api/TenpoService';
import { TenpoResponseDto as TenpoDetail } from 'api/generated/openapi';

/* Layout */
import './FormComponent.scss';
import SecondaryButton from 'util/SecondaryButton';
import { Container, Row, Col } from 'react-bootstrap';
import LoadingSpinner from 'util/LoadingSpinner';
import InfoRow from 'layout/InfoRow';

const ReservationDetailComponent: React.FC = () => {
  const navigate = useNavigate();
  const { reservationId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [tenpoDetail, setTenpoDetail] = useState<TenpoDetail>();
  const [reservationDetail, setReservationDetail] = useState<ReservationDetail>();

  useEffect(() => {
    const fetchReservationAndTenpo = async () => {
      try {
        const reservationDetailResult = await ReservationService.getReservation({
          reservationId: reservationId || '',
        });
        if (!reservationDetailResult.reservationId) return;
        setReservationDetail(reservationDetailResult);

        const tenpoDetailResult = await TenpoService.get({
          tenpoCode: reservationDetailResult?.tenpoCode ?? '',
        });
        setTenpoDetail(tenpoDetailResult);
      } catch (error) {
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    };
    fetchReservationAndTenpo();
  }, []);

  const handleClickReservationCancel = () => {
    navigate(`/reservation-cancel/${reservationId}`);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }
  if (!reservationDetail || !tenpoDetail) {
    navigate('/404');
  }

  const viewReservationContents = `${reservationDetail?.reservationTypeName}（${reservationDetail?.reservationTypeMinutes}分）`;
  const reservationDatetime = dayjs(reservationDetail?.reservationDateTime);
  const viewReservationDatetime = reservationDatetime?.format(
    `YYYY/MM/DD (${reservationDatetime.format('ddd')}) HH:mm〜`,
  );
  const viewTenpoName = `ホームメイト${tenpoDetail?.tenpoName}`;
  const tenpoUrl = tenpoDetail?.tenpoUrl;
  const viewTenpoPhone = tenpoDetail?.tenpoPhone;
  const viewTenpoEmail = tenpoDetail?.tenpoEmail;
  const isCancelled = reservationDetail?.reservationCancelled === 1;

  return (
    <>
      <Container className="pt-4 border-bottom">
        <InfoRow
          childrenLeft={<p className="fw-semibold">予約内容</p>}
          childrenRight={<p>{viewReservationContents}</p>}
        />
        <InfoRow
          childrenLeft={<p className="fw-semibold">日 時</p>}
          childrenRight={<p>{viewReservationDatetime}</p>}
        />
        <InfoRow
          childrenLeft={<p className="fw-semibold">店 舗</p>}
          childrenRight={
            <>
              <a href={tenpoUrl} target="_blank">
                {viewTenpoName}
              </a>
              <ul>
                <li>
                  <a href={`tel:${viewTenpoPhone}`}>{viewTenpoPhone} </a>
                </li>
                <li>
                  <a href={`mailto:${viewTenpoEmail}`}>{viewTenpoEmail}</a>
                </li>
              </ul>
            </>
          }
        />
        {isCancelled && (
          <InfoRow
            childrenLeft={<p className="fw-semibold">予約状況</p>}
            childrenRight={<p>キャンセル済み</p>}
          />
        )}
      </Container>
      <Container className="pt-4">
        <Row className="justify-content-center">
          <Col sm={6} xs={12}>
            <p className="fw-semibold">注意事項</p>
            <ul>
              <li className="fw-semibold">予約日時の変更について</li>
              <p>
                不動産会社側の都合で予約日時の変更をお願いする場合があります。予めご了承ください。
              </p>
              <li className="fw-semibold">予約時間について</li>
              <p>
                当店は予約制のため、予約時間が経過した場合、接客を終了させていただく場合がございます。
                また、ご予約の開始時間から30分経過しても、ご来店いただけず、ご連絡もない場合は予約キャンセルとさせていただくことがございます。{' '}
              </p>
              <li className="fw-semibold">予約の変更について</li>
              <p>現在の予約をキャンセルした上で、新たに予約を取得し直してください。</p>
              {!isCancelled && (
                <SecondaryButton
                  label="予約をキャンセルする"
                  type="button"
                  className="w-100 mt-1 mb-4"
                  onClick={handleClickReservationCancel}
                />
              )}
              <p>※予約当時のキャンセルは直接店舗にご連絡ください。</p>
            </ul>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default ReservationDetailComponent;
